import React, { useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { ComposableMap, Geographies, Geography, GeographiesProps, ZoomableGroup } from 'react-simple-maps';
import countries from 'i18n-iso-countries';
import nbLocale from 'i18n-iso-countries/langs/nb.json';
import worldTopoJson from 'world-atlas/countries-110m.json';
import { AuditEventCountryCountResponse } from 'api/adgang';

// Norske landnavn til tabell og kart-hover.
countries.registerLocale(nbLocale);

// react-simple-maps 5 parser TopoJSON, men typen på geography-propen tar bare med GeoJSON.
const worldTopo = worldTopoJson as unknown as GeographiesProps['geography'];

// Merkevarens teal-skala: lav -> høy aktivitet. Land uten data får en nøytral bakgrunn.
const MAP_LOW = '#9BD8D6';
const MAP_HIGH = '#004B46';
const MAP_EMPTY = '#EBF3F3';
const MAP_STROKE = '#FFFFFF';

const num = (v: unknown): number => Number(v ?? 0);

// Lineær interpolasjon mellom to hex-farger.
function lerpColor(a: string, b: string, t: number): string {
  const ah = a.replace('#', '');
  const bh = b.replace('#', '');
  const ar = parseInt(ah.slice(0, 2), 16);
  const ag = parseInt(ah.slice(2, 4), 16);
  const ab = parseInt(ah.slice(4, 6), 16);
  const br = parseInt(bh.slice(0, 2), 16);
  const bg = parseInt(bh.slice(2, 4), 16);
  const bb = parseInt(bh.slice(4, 6), 16);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${[r, g, bl].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

type CountryRow = {
  numericId: number | null;
  code: string;
  name: string;
  count: number;
  failed: number;
};

// Oversetter et lagret land (ISO alfa-2/alfa-3 eller fullt navn) til numerisk ISO-id (for kartet)
// og et visningsnavn (for tabellen). Robust mot ukjent format - ukjente vises fortsatt i tabellen.
function resolveCountry(raw: string): { numericId: number | null; name: string; code: string } {
  const value = (raw ?? '').trim();
  if (!value) return { numericId: null, name: 'Ukjent', code: '' };

  const upper = value.toUpperCase();
  let alpha2: string | undefined;
  if (upper.length === 2) alpha2 = upper;
  else if (upper.length === 3) alpha2 = countries.alpha3ToAlpha2(upper);
  else alpha2 = countries.getAlpha2Code(value, 'en') || countries.getAlpha2Code(value, 'nb');

  const numericStr = alpha2 ? countries.alpha2ToNumeric(alpha2) : undefined;
  const numericId = numericStr ? parseInt(numericStr, 10) : null;
  const name = alpha2 ? countries.getName(alpha2, 'nb') || value : value;
  return { numericId: Number.isNaN(numericId as number) ? null : numericId, name, code: alpha2 ?? upper };
}

function AuditLogGeography({ perCountry }: { perCountry?: AuditEventCountryCountResponse[] }) {
  const [sortKey, setSortKey] = useState<'count' | 'failed'>('count');
  const [hovered, setHovered] = useState<CountryRow | undefined>();

  const rows = useMemo<CountryRow[]>(
    () =>
      (perCountry ?? []).map((c) => {
        const resolved = resolveCountry(c.country ?? '');
        return {
          numericId: resolved.numericId,
          code: resolved.code,
          name: resolved.name,
          count: num(c.count),
          failed: num(c.failedCount)
        };
      }),
    [perCountry]
  );

  // Oppslag numerisk ISO-id -> rad, for fargelegging av kartet.
  const byNumericId = useMemo(() => {
    const map = new Map<number, CountryRow>();
    rows.forEach((r) => {
      if (r.numericId != null) map.set(r.numericId, r);
    });
    return map;
  }, [rows]);

  const maxCount = useMemo(() => rows.reduce((m, r) => Math.max(m, r.count), 0), [rows]);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => b[sortKey] - a[sortKey]),
    [rows, sortKey]
  );

  const colorFor = (count: number): string => {
    if (count <= 0 || maxCount <= 0) return MAP_EMPTY;
    // Kvadratrot sprer små verdier så de blir synlige.
    const t = Math.sqrt(count / maxCount);
    return lerpColor(MAP_LOW, MAP_HIGH, t);
  };

  const hasData = rows.length > 0;

  return (
    <Row className='pb-3'>
      <Col sm={12} lg={7} className='mb-3'>
        <div className='border rounded p-3 h-100'>
          <div className='fw-bold mb-2'>Innlogginger etter land</div>
          {hasData ? (
            <>
              <div className='text-muted mb-1' style={{ minHeight: '1.5em' }}>
                {hovered
                  ? `${hovered.name}: ${hovered.count.toLocaleString('no-NO')} innlogginger${
                      hovered.failed > 0 ? ` (${hovered.failed.toLocaleString('no-NO')} feilede)` : ''
                    }`
                  : 'Hold musepekeren over et land for detaljer'}
              </div>
              <ComposableMap
                projectionConfig={{ scale: 130 }}
                height={340}
                style={{ width: '100%', height: 'auto' }}
              >
                <ZoomableGroup zoom={1} minZoom={1} maxZoom={16}>
                  <Geographies geography={worldTopo}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const numericId = parseInt(String(geo.id), 10);
                        const entry = byNumericId.get(numericId);
                        return (
                          <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill={colorFor(entry?.count ?? 0)}
                            stroke={MAP_STROKE}
                            strokeWidth={0.3}
                            onMouseEnter={() => entry && setHovered(entry)}
                            onMouseLeave={() => setHovered(undefined)}
                            style={{ outline: 'none', opacity: entry && entry === hovered ? 0.8 : 1 }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
            </>
          ) : (
            <div
              className='text-muted d-flex align-items-center justify-content-center'
              style={{ height: 340 }}
            >
              Ingen innloggingsdata med land
            </div>
          )}
        </div>
      </Col>
      <Col sm={12} lg={5} className='mb-3'>
        <div className='border rounded p-3 h-100'>
          <div className='fw-bold mb-2'>Land</div>
          {hasData ? (
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              <table className='table table-sm'>
                <thead>
                  <tr>
                    <th>Land</th>
                    <th
                      role='button'
                      className='text-end'
                      onClick={() => setSortKey('count')}
                      style={{ cursor: 'pointer', fontWeight: sortKey === 'count' ? 700 : undefined }}
                    >
                      Innlogginger
                    </th>
                    <th
                      role='button'
                      className='text-end'
                      onClick={() => setSortKey('failed')}
                      style={{ cursor: 'pointer', fontWeight: sortKey === 'failed' ? 700 : undefined }}
                    >
                      Feilede
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r) => (
                    <tr key={r.code || r.name}>
                      <td>{r.name}</td>
                      <td className='text-end'>{r.count.toLocaleString('no-NO')}</td>
                      <td className='text-end' style={r.failed > 0 ? { color: '#CA0000', fontWeight: 600 } : undefined}>
                        {r.failed.toLocaleString('no-NO')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-muted'>Ingen data</div>
          )}
        </div>
      </Col>
    </Row>
  );
}

export default AuditLogGeography;
